
const VIDEO_EXTENSIONS = ['.mp4', '.webm', '.mov', '.avi', '.mkv'];
const GIF_EXTENSIONS = ['.gif'];

const DOCUMENT_EXTENSIONS = [
  '.pdf',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.ppt',
  '.pptx',
  '.csv',
  '.txt',
  '.rtf',
  '.odt',
  '.ods',
  '.odp',
  '.zip',
  '.pages',
  '.numbers',
  '.key',
];

function pathnameOf(url: string): string {
  try {
    return new URL(url).pathname;
  } catch {
    return url;
  }
}

function extensionOf(url: string): string {
  const path = pathnameOf(url).toLowerCase();
  const dot = path.lastIndexOf('.');
  return dot === -1 ? '' : path.slice(dot);
}

export function isVideoUrl(url: string): boolean {
  return VIDEO_EXTENSIONS.includes(extensionOf(url));
}

export function isGifUrl(url: string): boolean {
  return GIF_EXTENSIONS.includes(extensionOf(url));
}

/**
 * Only an explicit allow-list counts as a document. Anything unrecognised —
 * including a URL with no extension at all, which S3 keys sometimes are —
 * stays on the image path, so this can never turn a working picture into a
 * file card.
 */
export function isDocumentUrl(url: string): boolean {
  return DOCUMENT_EXTENSIONS.includes(extensionOf(url));
}

/** "PDF", "DOCX", … for the badge on a file card. */
export function fileExtensionLabel(url: string): string {
  return extensionOf(url).replace('.', '').toUpperCase() || 'FILE';
}

/**
 * A readable name for a stored file.
 *
 * Uploads are keyed with a millisecond timestamp prefix and the spaces
 * flattened to underscores — "1790297145501_questions_to_ask.pdf" — which is
 * unreadable in a list. Strip the prefix and put the spaces back, leaving the
 * extension alone.
 */
export function fileNameFromUrl(url: string): string {
  const last = pathnameOf(url).split('/').pop() ?? '';

  let name = last;
  try {
    name = decodeURIComponent(last);
  } catch {
    // A malformed escape sequence — the raw segment is still better than nothing.
  }

  name = name.replace(/^\d{9,}[-_]/, '');
  if (!name) return 'Attachment';

  const dot = name.lastIndexOf('.');
  if (dot <= 0) return name.replace(/_/g, ' ');
  return `${name.slice(0, dot).replace(/_/g, ' ')}${name.slice(dot)}`;
}

/** Splits a post's attachment URLs into things to display and files to list. */
export function splitAttachments<T extends { url: string }>(items: T[]) {
  const documents: T[] = [];
  const media: T[] = [];
  for (const item of items) {
    (isDocumentUrl(item.url) ? documents : media).push(item);
  }
  return { documents, media };
}
